import { z } from "zod";
export const WorkshopItemSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
});
export const WorkshopGroupSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    subtitle: z.string(),
    items: z.array(WorkshopItemSchema).min(1),
});
export const WorkshopDivisionSchema = z.object({
    code: z.string().min(1),
    label: z.string().min(1),
});
export const WorkshopConfigSchema = z.object({
    language: z.enum(["nb", "en"]),
    title: z.string().min(1),
    intro: z.string(),
    total_points: z.number().int().positive(),
    points_step: z.number().int().positive(),
    divisions: z.array(WorkshopDivisionSchema).min(1),
    groups: z.array(WorkshopGroupSchema).min(1),
});
export const WorkshopCodeSchema = z.object({
    code: z.string().trim().min(1).max(12),
});
export const WorkshopMineSchema = z.object({
    respondent_token: z.string().min(16).max(200),
});
export const WorkshopAnswerPayloadSchema = WorkshopMineSchema.extend({
    division: z.string().min(1).max(80),
    points: z.record(z.number()),
});
export const WorkshopPollWriteSchema = z.object({
    label: z.string().trim().min(1).max(200),
    source_md: z.string().min(1).max(100_000),
});
export const WorkshopPollUpdateSchema = z.object({
    label: z.string().trim().min(1).max(200).optional(),
    source_md: z.string().min(1).max(100_000).optional(),
});
export const WorkshopStatusSchema = z.object({
    status: z.enum(["draft", "open", "closed"]),
});
const DIVISION_HEADINGS = new Set(["divisions", "divisjoner"]);
function headingKey(title) {
    return title.trim().toLowerCase();
}
/** Parse a workshop template. `config` is set only when the file is valid. */
export function parseWorkshopTemplate(md) {
    const errors = [];
    const lines = md.replace(/\r\n/g, "\n").split("\n");
    let i = 0;
    const fm = {};
    if (lines[0]?.trim() === "---") {
        i = 1;
        let closed = false;
        while (i < lines.length) {
            const raw = lines[i] ?? "";
            if (raw.trim() === "---") {
                closed = true;
                i += 1;
                break;
            }
            const trimmed = raw.trim();
            if (trimmed && !trimmed.startsWith("#")) {
                const m = /^([A-Za-z0-9_]+):\s*(.*)$/.exec(trimmed);
                if (!m)
                    errors.push({ line: i + 1, message: "expected key: value" });
                else
                    fm[m[1]] = m[2].trim();
            }
            i += 1;
        }
        if (!closed)
            errors.push({ line: 1, message: "front matter is not closed" });
    }
    const language = fm.language || "nb";
    if (language !== "nb" && language !== "en") {
        errors.push({ line: 1, message: "language must be nb or en" });
    }
    const total_points = fm.total_points ? Number(fm.total_points) : 100;
    const points_step = fm.points_step ? Number(fm.points_step) : 5;
    if (!Number.isInteger(total_points) || total_points < 1) {
        errors.push({ line: 1, message: "total_points must be a positive whole number" });
    }
    if (!Number.isInteger(points_step) || points_step < 1 || points_step > total_points) {
        errors.push({ line: 1, message: "points_step must be a positive whole number up to the total" });
    }
    let title = "";
    let titleLine = 0;
    const introLines = [];
    const drafts = [];
    let current = null;
    const flushBlank = (buf) => buf.map((s) => s.trim()).filter(Boolean);
    for (; i < lines.length; i++) {
        const raw = lines[i] ?? "";
        const trimmed = raw.trim();
        if (trimmed.startsWith("# ") && !trimmed.startsWith("## ") && !title) {
            title = trimmed.slice(2).trim();
            titleLine = i + 1;
            continue;
        }
        if (trimmed.startsWith("## ")) {
            const name = trimmed.slice(3).trim();
            current = {
                title: name,
                titleLine: i + 1,
                subtitle: "",
                items: [],
                division: DIVISION_HEADINGS.has(headingKey(name)),
            };
            if (!name)
                errors.push({ line: i + 1, message: "heading is empty" });
            drafts.push(current);
            continue;
        }
        if (!current) {
            if (trimmed)
                introLines.push(trimmed);
            continue;
        }
        if (!trimmed)
            continue;
        if (trimmed.startsWith("- ")) {
            const label = trimmed.slice(2).trim();
            if (!label)
                errors.push({ line: i + 1, message: "list item is empty" });
            else
                current.items.push({ label, line: i + 1 });
            continue;
        }
        if (!current.division && !current.subtitle && current.items.length === 0) {
            current.subtitle = trimmed;
            continue;
        }
        errors.push({ line: i + 1, message: "expected a list item" });
    }
    if (!title)
        errors.push({ line: titleLine || 1, message: "title is missing" });
    const groups = [];
    const divisions = [];
    let groupN = 0;
    const seenCodes = new Set();
    for (const draft of drafts) {
        if (draft.division) {
            if (draft.items.length === 0) {
                errors.push({ line: draft.titleLine, message: "divisions list is empty" });
            }
            for (const item of draft.items) {
                const m = /^([A-Za-z0-9_]+):\s*(.+)$/.exec(item.label);
                if (!m) {
                    errors.push({ line: item.line, message: "expected code: Label" });
                    continue;
                }
                const code = m[1];
                if (seenCodes.has(code)) {
                    errors.push({ line: item.line, message: `duplicate division ${code}` });
                    continue;
                }
                seenCodes.add(code);
                divisions.push({ code, label: m[2].trim() });
            }
            continue;
        }
        groupN += 1;
        if (draft.items.length === 0) {
            errors.push({ line: draft.titleLine, message: "group has no statements" });
            continue;
        }
        groups.push({
            id: `g${groupN}`,
            title: draft.title,
            subtitle: draft.subtitle,
            items: draft.items.map((item, idx) => ({
                id: `g${groupN}_${idx + 1}`,
                label: item.label,
            })),
        });
    }
    if (groups.length === 0)
        errors.push({ line: 1, message: "at least one statement group is required" });
    if (divisions.length === 0)
        errors.push({ line: 1, message: "a divisions list is required" });
    if (errors.length)
        return { config: null, errors };
    const parsed = WorkshopConfigSchema.safeParse({
        language,
        title,
        intro: flushBlank(introLines).join("\n"),
        total_points,
        points_step,
        divisions,
        groups,
    });
    if (!parsed.success) {
        return {
            config: null,
            errors: parsed.error.issues.map((issue) => ({ line: 1, message: issue.message })),
        };
    }
    return { config: parsed.data, errors: [] };
}
export function workshopItemIds(config) {
    return config.groups.flatMap((g) => g.items.map((item) => item.id));
}
export function validateWorkshopAnswer(config, answer) {
    if (!config.divisions.some((d) => d.code === answer.division))
        return "invalid_division";
    const ids = workshopItemIds(config);
    const keys = Object.keys(answer.points);
    if (keys.length !== ids.length || ids.some((id) => !Object.prototype.hasOwnProperty.call(answer.points, id))) {
        return "invalid_items";
    }
    let sum = 0;
    for (const id of ids) {
        const n = answer.points[id];
        if (typeof n !== "number" || !Number.isInteger(n) || n < 0 || n > config.total_points) {
            return "invalid_points";
        }
        sum += n;
    }
    if (sum !== config.total_points)
        return "points_total";
    return null;
}
function sliceFor(config, rows) {
    const n = rows.length;
    const items = config.groups.flatMap((g) => g.items.map((item) => {
        if (n === 0)
            return { id: item.id, label: item.label, group_id: g.id, average: null };
        const sum = rows.reduce((acc, row) => acc + (row.points[item.id] ?? 0), 0);
        return { id: item.id, label: item.label, group_id: g.id, average: sum / n };
    }));
    const groups = config.groups.map((g) => {
        if (n === 0)
            return { id: g.id, title: g.title, total: null };
        const total = items
            .filter((item) => item.group_id === g.id)
            .reduce((acc, item) => acc + (item.average ?? 0), 0);
        return { id: g.id, title: g.title, total };
    });
    return { n, items, groups };
}
export function summarizeWorkshopAnswers(config, answers) {
    return {
        all: sliceFor(config, answers),
        divisions: config.divisions.map((division) => ({
            code: division.code,
            label: division.label,
            slice: sliceFor(config, answers.filter((a) => a.division === division.code)),
        })),
    };
}
//# sourceMappingURL=workshop.js.map